import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Button,
  Paper,
  Box,
  TextField,
  CircularProgress
} from "@material-ui/core";
import { useFormik } from "formik";
import RawBreadCrumbs from "@/components/BreadCrumbs";
import { useTranslation } from "@/langs/useTranslation";
import StripedTitle from "@/components/StripedTitle";
import { FormItem } from "@/components/Form";
import DuringExecute from "./reduce/DuringExecute";
import LoopMode from "./reduce/LoopMode";
import ExecuteTime from "./reduce/ExecuteTime";
import DisplayContent from "./reduce/DisplayContent";
import * as Yup from "yup";
import { fullWindowTemplateId } from "./CONSTANT";
import {
  createCMSTemplate,
  editCMSTemplate,
  getTemplateDetail
} from "@/apis/apiCollection";
import { useHistory } from "react-router-dom";
import { toast } from "@/apis/request";
import { useParams } from "react-router-dom";
import { getDayEndTime, getDayStartTime } from "@/helpers/utils";

const CMSFullScreenTemplate = () => {
  const { t, userLanguage } = useTranslation();
  const { id: templateId, type = "create" } = useParams();
  const [loading, setLoading] = useState(false);
  const readonly = type === "view";
  const history = useHistory();

  const initialValues = {
    name: "",
    effectType: "1",
    effectEndTime: getDayEndTime(),
    effectBeginTime: getDayStartTime(),
    effectTimes: 1,
    contentTemplateDetail: {
      loopMode: "daily",
      loopDailyValue: "every_day",
      loopModeDays: [],
      executeTimeRangeInfo: [],
      resources: []
    }
  };

  const { values, handleChange, handleSubmit, touched, errors, setValues } =
    useFormik({
      initialValues,
      onSubmit: async () => {
        if (values.contentTemplateDetail.resources.length === 0) {
          toast.error(t({defaultMessage: "必须选择一个播放内容"}))
          return
        }
        if (values.effectType === "2" && values.effectBeginTime > values.effectEndTime) {
          toast.error(t({defaultMessage: "起始时间不能大于结束时间"}))
          return
        }
        const newValue = {
          ...values,
          effectType: Number(values.effectType),
          contentTemplateTypeId: fullWindowTemplateId,
          contentTemplateDetail: {
            ...values.contentTemplateDetail,
            resources: values.contentTemplateDetail.resources.map(v => ({
              id: v.id || "",
              resourceId: v.resourceId,
              sort: v.sort,
              playDuration: v.duration
            }))
          }
        };
        newValue.contentTemplateDetail = JSON.stringify(
          newValue.contentTemplateDetail
        );
        if (type === "create") await createCMSTemplate(newValue);
        if (type === "edit") {
          newValue.id = templateId;
          await editCMSTemplate(newValue);
        }
        history.goBack();
        toast.success(t({ defaultMessage: "创建成功" }));
      },
      validationSchema: Yup.object({
        name: Yup.string().required("必填项"),
        contentTemplateDetail: Yup.object({
          executeTimeRangeInfo: Yup.array().min(
            1,
            t({ defaultMessage: "至少添加一个执行时间" })
          )
        })
      })
    });

  const BreadCrumbs = useMemo(
    () =>
      RawBreadCrumbs([
        {
          name: t({ defaultMessage: "内容模板列表" }),
          to: "/cms/template/list"
        }
      ]),
    []
  );

  const pageName = useMemo(() => {
    switch (type) {
      case "create":
        return t({ defaultMessage: "新建模板" });
      case "edit":
        return t({ defaultMessage: "编辑模版" });
      case "view":
        return t({ defaultMessage: "预览模板" });
      default:
    }
  }, [userLanguage, type]);

  useEffect(() => {
    if (type != "create" && templateId) {
      setLoading(true);
      getTemplateDetail({
        id: templateId
      }).then(res => {
        try {
          const detail = JSON.parse(res.contentTemplateDetail);
          setValues({
            name: res.name,
            effectType: res.effectType.toString(),
            effectEndTime: Number(res.effectEndTime),
            effectBeginTime: Number(res.effectBeginTime),
            effectTimes: res.effectTimes ? Number(res.effectTimes) : undefined,
            contentTemplateDetail: {
              executeTimeRangeInfo: detail.executeTimeRangeInfo
                ? detail.executeTimeRangeInfo.map(v => ({
                    executeBeginTime: v.executeBeginTime,
                    executeEndTime: v.executeEndTime
                  }))
                : [],
              loopDailyValue: detail.loopDailyValue,
              loopMode: detail.loopMode,
              loopModeDays: detail.loopModeDays || [],
              resources: detail.resources
                ? detail.resources.map(v => ({
                    id: v.id,
                    resourceId: v.resourceId,
                    sort: v.sort,
                    duration: v.playDuration,
                    name: v.name,
                    s3Path: v.s3Path,
                    mimeType: v.mimeType,
                    width: v.width,
                    height: v.height,
                    size: `${v.width}*${v.height}`,
                    previewType: (v.mimeType || "").split("/")[0],
                    type: (v.mimeType || "").split("/")[1]
                  }))
                : []
            }
          });
        } catch (e) {
        } finally {
          setLoading(false);
        }
      });
    }
  }, [type, templateId]);

  return (
    <div>
      <BreadCrumbs pageName={pageName} />
      <Paper elevation={0}>
        <form style={{ flex: 1 }} onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
            <Typography variant="h6">{pageName}</Typography>
            {!readonly && (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginLeft: "auto" }}
              >
                {t({ defaultMessage: "确定" })}
              </Button>
            )}
            <Button
              variant="outlined"
              // type="submit"
              style={{ marginLeft: readonly ? "auto" : 16 }}
              onClick={() => {
                history.goBack();
              }}
            >
              {t({ defaultMessage: "取消" })}
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              style={{ marginTop: 50 }}
            >
              <div style={{ width: 1000 }}>
                <StripedTitle>{t({ defaultMessage: "基本信息" })}</StripedTitle>
                <FormItem
                  label={
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: "bold" }}
                    >
                      {t({ defaultMessage: "内容名称" })}
                    </Typography>
                  }
                  required
                  row
                  colon={false}
                >
                  <TextField
                    variant="outlined"
                    name="name"
                    onChange={handleChange}
                    style={{ width: "100%", background: "#fff" }}
                    value={values.name}
                    size="small"
                    // inputProps={{ maxLength: 30 }}
                    disabled={readonly}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: "bold" }}
                    >
                      {t({ defaultMessage: "执行期间" })}
                    </Typography>
                  }
                  row
                  colon={false}
                >
                  <DuringExecute
                    values={values}
                    handleChange={handleChange}
                    readonly={readonly}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: "bold" }}
                    >
                      {t({ defaultMessage: "循环模式" })}
                    </Typography>
                  }
                  row
                  colon={false}
                >
                  <LoopMode
                    values={values.contentTemplateDetail}
                    handleChange={handleChange}
                    readonly={readonly}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: "bold" }}
                    >
                      {t({ defaultMessage: "执行时间" })}
                    </Typography>
                  }
                  row
                  colon={false}
                >
                  <ExecuteTime
                    value={values.contentTemplateDetail.executeTimeRangeInfo}
                    handleChange={handleChange}
                    readonly={readonly}
                    touched={touched}
                    errors={errors}
                  />
                </FormItem>
                <FormItem
                  label={
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: "bold" }}
                    >
                      {t({ defaultMessage: "播放内容" })}
                    </Typography>
                  }
                  row
                  colon={false}
                >
                  <DisplayContent
                    value={values.contentTemplateDetail.resources}
                    handleChange={handleChange}
                    readonly={readonly}
                  />
                </FormItem>
              </div>
            </Box>
          )}
        </form>
      </Paper>
    </div>
  );
};

export default CMSFullScreenTemplate;
